export const CREATOR_CLOUD_UNAVAILABLE_UNTIL_KEY = 'fg_creator_cloud_unavailable_until_ms'
const CREATOR_CLOUD_UNAVAILABLE_REASON_KEY = 'fg_creator_cloud_unavailable_reason'

export type CloudUnavailableReason = 'backend-database-timeout' | 'backend-unavailable'

const lowerErrorText = (error: any): string =>
  `${String(error?.message || error || '')} ${String(error?.body || '')}`.toLowerCase()

export function getCloudUnavailableReasonFromError(error: any): CloudUnavailableReason | null {
  const status = Number(error?.status ?? NaN)
  const text = lowerErrorText(error)
  const isBackendPoolTimeout =
    Number.isFinite(status) &&
    (status === 500 || status === 503) &&
    (
      text.includes('db_pool_timeout') ||
      text.includes('pool timed out') ||
      text.includes('timed out while waiting for an open connection') ||
      (text.includes('tableau write') && text.includes('timeout'))
    )
  if (isBackendPoolTimeout) return 'backend-database-timeout'

  const isNetworkFailure =
    text.includes('failed to fetch') ||
    text.includes('networkerror') ||
    text.includes('econnrefused') ||
    text.includes("couldn't connect to server") ||
    text.includes('proxy error')
  if (isNetworkFailure) return 'backend-unavailable'

  if (Number.isFinite(status) && status >= 500) return 'backend-unavailable'
  return null
}

export function isBackendPoolTimeoutError(error: any): boolean {
  return getCloudUnavailableReasonFromError(error) === 'backend-database-timeout'
}

export function isCloudBackendUnavailableError(error: any): boolean {
  return getCloudUnavailableReasonFromError(error) !== null
}

export function readCloudUnavailableUntilMs(): number {
  if (typeof window === 'undefined') return 0
  try {
    const raw = Number(window.localStorage.getItem(CREATOR_CLOUD_UNAVAILABLE_UNTIL_KEY) || 0)
    return Number.isFinite(raw) ? raw : 0
  } catch {
    return 0
  }
}

export function readCloudUnavailableReason(): CloudUnavailableReason | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = String(window.localStorage.getItem(CREATOR_CLOUD_UNAVAILABLE_REASON_KEY) || '').trim()
    return raw === 'backend-database-timeout' || raw === 'backend-unavailable' ? raw : null
  } catch {
    return null
  }
}

export function writeCloudUnavailableUntilMs(
  nextMs: number,
  reason: CloudUnavailableReason | null = null,
): void {
  if (typeof window === 'undefined') return
  try {
    if (nextMs > Date.now()) {
      window.localStorage.setItem(CREATOR_CLOUD_UNAVAILABLE_UNTIL_KEY, String(Math.floor(nextMs)))
      if (reason) window.localStorage.setItem(CREATOR_CLOUD_UNAVAILABLE_REASON_KEY, reason)
      else window.localStorage.removeItem(CREATOR_CLOUD_UNAVAILABLE_REASON_KEY)
    } else {
      window.localStorage.removeItem(CREATOR_CLOUD_UNAVAILABLE_UNTIL_KEY)
      window.localStorage.removeItem(CREATOR_CLOUD_UNAVAILABLE_REASON_KEY)
    }
  } catch {
    // ignore storage access failures
  }
}

export function isCloudUnavailableBackoffActive(): boolean {
  return readCloudUnavailableUntilMs() > Date.now()
}

export function markCloudUnavailableFromError(error: any, backoffMs = 30_000): boolean {
  const reason = getCloudUnavailableReasonFromError(error)
  if (!reason) return false
  writeCloudUnavailableUntilMs(Date.now() + Math.max(1_000, Math.floor(backoffMs)), reason)
  return true
}

export function cloudSaveRetryMessage(reason: CloudUnavailableReason | null): string {
  if (reason === 'backend-database-timeout') {
    return 'Waiting to retry after backend database timeout'
  }
  if (reason === 'backend-unavailable') {
    return 'Waiting to retry after backend connection failed'
  }
  return 'Waiting to retry after cloud service error'
}

export function cloudHomeUnavailableMessage(
  reason: CloudUnavailableReason | null,
  allowLocalRecents: boolean,
): string {
  const suffix = allowLocalRecents ? ' Showing local recents only.' : ''
  if (reason === 'backend-database-timeout') {
    return `Cloud workbooks are temporarily unavailable (database connection pool timeout).${suffix}`
  }
  if (reason === 'backend-unavailable') {
    return `Unable to reach the cloud API right now.${suffix}`
  }
  return `Cloud workbooks are temporarily unavailable.${suffix}`
}
